import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@src/common/Button';
import CanvasDraw from 'react-canvas-draw';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useTinRegistration } from '@src/state/tinRegistration';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useForm as useAbyssForm } from '@uhg-abyss/web/hooks/useForm';
import { config } from '@uhg-abyss/web/tools/config';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { useUser } from '@src/state/user/hooks';
import { userAgent } from '@uhg-abyss/web/tools/userAgent';

const UploadingMessage = styled('div', {
  marginTop: '4px',
  fontSize: '13px',
  color: '$accent1',
});

const UploadSuccessMessage = styled('div', {
  marginTop: '4px',
  fontSize: '13px',
  color: '$success1',
});

const UploadErrorMessage = styled('div', {
  marginTop: '4px',
  fontSize: '13px',
  color: '$error1',
});
export const DrawSignature = ({
  id,
  model,
  styles,
  fileType,
  width,
  height,
  validators,
  enableUndo,
  enableClear,
  enableSave,
}) => {
  const { uploadProof, setDocumentId, setSignatureCanvas, uploadSignature } =
    useTinRegistration.actions();
  const { signatureCanvas, uploadProofSaga } = useTinRegistration.state();
  const abyssForm = useAbyssForm();
  const textSignature = abyssForm.getValues('attestation.textSignature');
  const registration = abyssForm.getValues('searchRegistration');
  const { trackingNumber, documentIds } = useTinRegistration.state();

  const [signatureRef, setSignatureRef] = useState();
  const [signature, setSignature] = useState();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadFail, setUploadFail] = useState(false);
  const [disableSaveSign, setDisableSaveSign] = useState(false);
  const userInfo = useUser.state();
  const { isCheckoutUser } = userInfo;

  const handleChange = () => {
    setSignature(signatureRef.getSaveData());
    setSignatureCanvas(signatureRef.getSaveData());
    abyssForm.setValue('attestation.signature', 'signature');
  };

  const handleClear = () => {
    setDocumentId({ id: 'drawSignature', value: undefined });
    setDisableSaveSign(false);
    storage.local.remove('signature-draw');
    signatureRef.clear();
    setUploadFail(false);
    setUploadSuccess(false);
    setSignature(null);

    const interactionName = 'attestation widget';
    const interactionValue = 'clear signature';
    const eventInteraction = 'interaction';
    const interactionContext = 'draw';
    adobeTaggingEvent('CLEAR_SIGNATURE', {
      eventInteraction,
      interactionName,
      interactionValue,
      interactionContext,
    });

    abyssForm.setValue('attestation.signature', '');
  };

  const getBinaryFile = (img) => {
    const blobBin = atob(img.split(',')[1]);
    const array = [];
    for (let i = 0; i < blobBin.length; i++) {
      array.push(blobBin.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], {
      type: 'image/png',
      encoding: 'utf-8',
    });
  };

  const handleUploadResponse = (resp) => {
    if (resp && resp.doc360Id) {
      setUploadSuccess(true);
    } else {
      setUploadFail(true);
    }
  };

  const handleSave = () => {
    if (!signature) {
      return;
    }
    const sourceCanvas = document.getElementsByTagName('canvas')[1];
    const destinationCanvas = document.createElement('canvas');
    destinationCanvas.height = sourceCanvas.height;
    destinationCanvas.width = sourceCanvas.width;

    const destContext = destinationCanvas.getContext('2d');

    destContext.drawImage(sourceCanvas, 0, 0);
    destContext.globalCompositeOperation = 'destination-over';
    destContext.fillStyle = 'white';
    destContext.fillRect(
      0,
      0,
      destinationCanvas.width,
      destinationCanvas.height
    );

    const image = destinationCanvas.toDataURL(fileType);
    const blob = getBinaryFile(image);
    storage.local.set('signature-draw', image);
    const tNum = trackingNumber;
    let file;
    if (!userAgent.isIE) {
      file = new File([blob], `Signature-${tNum}.png`, {
        type: 'image/png',
      });
    }
    const reqBody = {
      billingTin: registration.billingTin,
      requestId: registration.requestId,
      fileType: 'US',
      trackingNumber: tNum,
      file: userAgent.isIE ? blob : file,
      test: isCheckoutUser,
    };

    if (userAgent.isIE) {
      uploadSignature(
        reqBody,
        (resp) => {
          abyssForm.setValue('attestation.signature', 'saved', {
            errors: { requierd: false, save: false },
            touched: true,
          });
          setDisableSaveSign(true);
          setDocumentId({ id: 'drawSignature', value: resp.doc360Id });
          if (config('US3830334_SIGNATURE_FIX')) {
            handleUploadResponse(resp);
          } else {
            setUploadSuccess(true);
          }
        },
        (error) => {
          setUploadFail(true);
        }
      );
    } else {
      uploadProof(
        reqBody,
        (resp) => {
          abyssForm.setValue('attestation.signature', 'saved', {
            errors: { requierd: false, save: false },
            touched: true,
          });
          setDisableSaveSign(true);
          setDocumentId({ id: 'drawSignature', value: resp.doc360Id });
          if (config('US3830334_SIGNATURE_FIX')) {
            handleUploadResponse(resp);
          } else {
            setUploadSuccess(true);
          }
        },
        (error) => {
          setUploadFail(true);
        }
      );
    }
  };

  const defaultStyle = {
    cursor: 'crosshair',
    border: '1px solid black',
    borderRadius: '3px',
  };

  useEffect(() => {
    if (
      signatureCanvas &&
      signatureRef &&
      storage.local.get('signature-draw')
    ) {
      signatureRef.loadSaveData(signatureCanvas);
    }
  }, [signatureRef]);

  useEffect(() => {
    if (!textSignature || textSignature === '') {
      setDocumentId({ id: 'textSignature', value: undefined });
    }
  }, []);

  useEffect(() => {
    abyssForm.setValue('attestation.saveSignature', undefined, {
      errors: { required: true },
      touched: false,
    });
  }, []);

  useEffect(() => {
    if (documentIds && documentIds.drawSignature) {
      abyssForm.setValue('attestation.signature', documentIds.drawSignature);
    }
  }, []);

  return (
    <React.Fragment>
      <CanvasDraw
        id={id}
        style={{
          ...defaultStyle,
          ...styles,
        }}
        canvasWidth={width}
        canvasHeight={height}
        hideInterface
        brushRadius={1}
        lazyRadius={0}
        hideGrid
        brushColor="black"
        onChange={handleChange}
        ref={(ref) => {
          return setSignatureRef(ref);
        }}
      />
      <div style={{ marginLeft: '0.75%' }}>
        {enableSave && (
          <Button
            variant="tertiary"
            style={{ marginTop: '10px' }}
            onClick={handleSave}
            isDisabled={disableSaveSign}
          >
            Save Signature
          </Button>
        )}
        {enableClear && (
          <Button
            variant="tertiary"
            style={{ marginTop: '10px', marginLeft: '14px' }}
            onClick={handleClear}
          >
            Clear
          </Button>
        )}
        {enableUndo && (
          <Button
            variant="tertiary"
            style={{ marginLeft: '14px', marginTop: '10px' }}
            onClick={() => {
              return signatureRef.undo();
            }}
          >
            Undo
          </Button>
        )}
      </div>
      {uploadProofSaga.isLoading && (
        <UploadingMessage id="saving-signature-message">
          Saving signature, please wait...
        </UploadingMessage>
      )}
      {uploadSuccess && (
        <UploadSuccessMessage id="saving-signature-upload-success-message">
          Signature has been saved successfully.
        </UploadSuccessMessage>
      )}
      {uploadFail && (
        <UploadErrorMessage id="saving-signature-upload-failed-message">
          Failed to save signature, please clear and try again.
        </UploadErrorMessage>
      )}
    </React.Fragment>
  );
};
DrawSignature.propTypes = {
  id: PropTypes.string.isRequired,
  model: PropTypes.string.isRequired,
  styles: PropTypes.shape({}),
  validators: PropTypes.shape({}),
  fileType: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  enableUndo: PropTypes.bool,
  enableSave: PropTypes.bool,
  enableClear: PropTypes.bool,
};

DrawSignature.defaultProps = {
  styles: {},
  validators: null,
  enableUndo: false,
  enableSave: false,
  enableClear: false,
};
